import { useEffect, useMemo } from 'react';
import { useAnalysisData } from '../../contexts/AnalysisDataContext';
import { AnalysisRange } from './useAnalysisRange';
import {
  collectedSeries,
  failureRate,
  failuresByErrorCode,
  failuresByGateway,
  isFailedPayment,
  medianHoursToPayment,
  methodMix,
  outstandingBalances,
  paymentsInWindow,
  refundSeries,
} from '../../utils/analytics/paymentMetrics';
import { Granularity } from '../../utils/analytics/types';
import { sumBy } from '../../utils/analytics/aggregations';
import { ComparisonMode, comparisonRange, makeDelta } from '../../utils/analytics/comparison';

export function usePaymentAnalysis(
  range: AnalysisRange,
  granularity: Granularity,
  comparison: ComparisonMode = 'none'
) {
  const { stays, payments, loading, error, ensureRange } = useAnalysisData();
  const prevRange = useMemo(() => comparisonRange(range, comparison), [range, comparison]);

  useEffect(() => {
    ensureRange(prevRange ? prevRange.from : range.from, range.to);
  }, [range.from, range.to, prevRange, ensureRange]);

  return useMemo(() => {
    const { from, to } = range;
    const windowPayments = paymentsInWindow(payments, from, to);
    const collected = collectedSeries(windowPayments, from, to, granularity);
    const refunds = refundSeries(windowPayments, from, to, granularity);
    const collectedTotal = sumBy(collected, p => p.value);
    const refundTotal = sumBy(refunds, p => p.value);
    const currentFailureRate = failureRate(windowPayments);

    let deltas: {
      collected?: ReturnType<typeof makeDelta>;
      refunds?: ReturnType<typeof makeDelta>;
      failureRate?: ReturnType<typeof makeDelta>;
    } | null = null;
    if (prevRange) {
      const priorPayments = paymentsInWindow(payments, prevRange.from, prevRange.to);
      deltas = {
        collected: makeDelta(
          collectedTotal,
          sumBy(collectedSeries(priorPayments, prevRange.from, prevRange.to, granularity), p => p.value)
        ),
        refunds: makeDelta(
          refundTotal,
          sumBy(refundSeries(priorPayments, prevRange.from, prevRange.to, granularity), p => p.value),
          false
        ),
        failureRate: makeDelta(currentFailureRate, failureRate(priorPayments), false),
      };
    }

    return {
      loading,
      error,
      failureRate: currentFailureRate,
      failedCount: windowPayments.filter(isFailedPayment).length,
      collectedTotal,
      refundTotal,
      deltas,
      collected,
      refunds,
      byErrorCode: failuresByErrorCode(windowPayments),
      byGateway: failuresByGateway(windowPayments),
      mix: methodMix(windowPayments),
      medianHoursToPaid: medianHoursToPayment(stays, payments),
      // Balance aging looks at all loaded stays/payments, not just the window:
      // an old unpaid balance is exactly what the manager needs to see.
      outstanding: outstandingBalances(stays, payments),
    };
  }, [stays, payments, range, prevRange, granularity, loading, error]);
}
